<template>
  <div
    class="video-player"
    :class="{ ready: ready, 'is-fullscreen': video.state.isFullscreen }"
    @click="sound.dialog = false"
    @mousemove="handleUserActivity"
    @touchstart.passive="handleUserActivity"
    @mouseleave="handlePlayerMouseLeave"
  >
    <div class="video-player__aspect-ratio">
      <div
        class="video-player__video"
        @mouseenter="handleVideoMouseEnter"
        @mouseleave="handleVideoMouseLeave"
      >
        <div
          v-if="(coverImage && !isCoverLoaded && !isTimeout) || !ready || loading"
          class="video-player__loader-cover"
        >
          <div class="skeleton-cover"></div>
        </div>

        <div v-if="coverImage" class="video-player__cover" ref="coverRef">
          <img
            :src="coverSrc || coverImage"
            :alt="title || ''"
            loading="lazy"
            decoding="async"
            @load="onCoverLoad"
            @error="onCoverError"
          />
        </div>

        <div v-if="loading" class="video-player__loader"><div class="spinner-loader"></div></div>
        <video
          class="video-player__video__element"
          preload="metadata"
          ref="videoElement"
          :muted="video.state.muted"
          @loadedmetadata="onLoadedMetadata"
          @timeupdate="timeUpdateFunction"
          @canplay="onVideoCanPlay"
          @play="onVideoPlay"
          @pause="onVideoPause"
          @click="togglePlay"
          @waiting="onVideoWaiting"
          @stalled="onVideoStalled"
          @playing="onVideoPlaying"
          @seeking="onVideoSeeking"
          @seeked="onVideoSeeked"
        >
          <source
            v-for="source in videoSources"
            :key="source.src"
            :src="source.src"
            :type="source.type"
          />
          Текущий браузер не поддерживает воспроизведение этого видео.
        </video>
        <template v-if="!hideVideoControls">
          <div
            class="video-player__video__control left"
            :class="{ visible: shouldShowControls }"
            @click.stop="timeChangeBack"
            @mouseenter="onControlMouseEnter"
            @mouseleave="onControlMouseLeave"
            style="display: none"
          ></div>
          <div
            class="video-player__video__control right"
            :class="{ visible: shouldShowControls }"
            @click.stop="timeChangeFw"
            @mouseenter="onControlMouseEnter"
            @mouseleave="onControlMouseLeave"
            style="display: none"
          ></div>
          <div
            class="video-player__play-pause-button"
            :class="{ visible: shouldShowPlayPauseButton }"
            @click.stop="togglePlay"
            @touchstart.stop="handlePlayButtonTouch"
            @mouseenter="onControlMouseEnter"
            @mouseleave="onControlMouseLeave"
          >
            <svg
              v-if="video.state.playing === 'playing'"
              class="video-player__play-pause-icon"
              viewBox="0 0 24 24"
              fill="white"
            >
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
            <svg v-else class="video-player__play-pause-icon" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </template>
        <div v-if="isBuffering" class="video-buffering">
          <div class="video-buffering__overlay"></div>
          <div class="video-buffering__spinner">
            <div class="spinner"></div>
          </div>
        </div>
      </div>
    </div>
    <div
      :class="{
        playing: video.state.playing === 'playing',
        loading: loading,
        'hidden-down': shouldHideBottomControls,
      }"
      class="video-player__controls"
      ref="videoControls"
      @mouseenter="handleControlsMouseEnter"
      @mouseleave="handleControlsMouseLeave"
    >
      <div class="progressbar">
        <div class="progressbar__time start">
          {{ formatTime(video.state.timeNow).minutes }}:{{
            formatTime(video.state.timeNow).seconds
          }}
        </div>
        <div class="progressbar__center">
          <div
            class="progressbar__progress"
            :style="{ backgroundSize: (video.state.position || 0) + '% 100%' }"
          >
            <div class="trumb" :style="{ left: (video.state.position || 0) + '%' }"></div>
          </div>
          <input
            class="progressbar__seek"
            min="0"
            :max="video.state.length"
            type="range"
            step="1"
            v-model="video.state.timeNow"
            @input="seekTo"
          />
        </div>
        <div class="progressbar__time end">
          {{ formatTime(video.state.length).minutes }}:{{ formatTime(video.state.length).seconds }}
        </div>
      </div>
      <div class="video-player__buttons">
        <div
          class="video-player__volume-container"
          @mouseenter="showVolumeSlider = true"
          @mouseleave="handleVolumeSliderLeave"
        >
          <button
            class="video-player__button video-player__button--sound"
            @click="toggleMute"
            :aria-label="video.state.muted ? 'Включить звук' : 'Выключить звук'"
          >
            <svg
              v-if="video.state.muted"
              class="video-player__icon"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
              <line x1="23" y1="9" x2="17" y2="15"></line>
              <line x1="17" y1="9" x2="23" y2="15"></line>
            </svg>
            <svg
              v-else
              class="video-player__icon"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
            </svg>
          </button>
          <div
            class="video-player__volume-slider"
            :class="{ 'video-player__volume-slider--visible': showVolumeSlider }"
          >
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              :value="volumeSliderValue"
              @input="setVolume"
              class="video-player__volume-input"
              aria-label="Громкость"
            />
          </div>
        </div>
        <button
          class="video-player__button video-player__button--fullscreen"
          @click="toggleFullscreen"
          :aria-label="
            video.state.isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'
          "
        >
          <svg
            v-if="video.state.isFullscreen"
            class="video-player__icon"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"
            ></path>
          </svg>
          <svg
            v-else
            class="video-player__icon"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { showToast } from '@/utils/toast'

export default {
  name: 'ProjectVideoPlayer',
  props: {
    videoSources: {
      type: Array,
      required: true,
      validator(sources) {
        return sources.every((source) => source.src && source.type)
      },
    },
    title: {
      type: String,
      default: 'Video Title',
    },
    loader: {
      type: String,
      default: 'Loading...',
    },
    hideVideoControls: {
      type: Boolean,
      default: false,
    },
    autoPlayOnHover: {
      type: Boolean,
      default: false,
    },
    coverImage: {
      type: [String, null],
      default: null,
    },
  },
  data() {
    return {
      ready: false,
      loading: true,
      showVolumeSlider: false,
      showControls: false,
      controlsHideTimer: null,
      controlsInactivityTimer: null,
      controlsAutoHidden: false,
      isMouseOverVideo: false,
      isMouseOverControls: false,
      hasPlayedOnce: false,
      wasMouseOnVideo: false,
      isBuffering: false,
      bufferingCheckInterval: null,
      volumeSliderValue: 1,
      volumeAnimationTimer: null,
      sound: {
        dialog: false,
      },
      video: {
        state: {
          playing: false,
          timeNow: 0,
          length: 0,
          position: 0,
          muted: false,
          isFullscreen: false,
          volume: 1,
        },
      },
      isCoverLoaded: false,
      isCoverError: false,
      coverSrc: '',
      isTimeout: false,
    }
  },
  computed: {
    shouldShowControls() {
      // Показываем кнопки, если мышь наведена или видео на паузе
      return this.showControls || this.video.state.playing === 'paused'
    },
    shouldShowPlayPauseButton() {
      // Показываем кнопку плей/пауза если видео еще не запускалось, или по обычной логике
      if (!this.hasPlayedOnce) {
        return true
      }
      return this.shouldShowControls
    },
    shouldHideBottomControls() {
      return (
        this.hasPlayedOnce &&
        this.video.state.playing === 'playing' &&
        this.controlsAutoHidden &&
        !this.isMouseOverControls &&
        !this.showVolumeSlider
      )
    },
  },
  mounted() {
    this.ready = true
    this.setupFullscreenListeners()

    // Загружаем обложку если она есть
    if (this.coverImage) {
      this.loadAndCacheCover()

      // Убеждаемся, что обложка видна по умолчанию (если видео не играет)
      // Используем несколько попыток, чтобы убедиться, что ref готов
      this.$nextTick(() => {
        if (this.video.state.playing !== 'playing' && !this.isMouseOverVideo) {
          this.showCover()
        }
      })

      // Также проверяем после небольшой задержки, чтобы убедиться, что обложка показывается
      setTimeout(() => {
        if (this.coverImage && this.video.state.playing !== 'playing' && !this.isMouseOverVideo) {
          this.showCover()
        }
      }, 100)

      // Таймаут для проверки загрузки обложки
      setTimeout(() => {
        if (!this.isCoverLoaded) {
          this.isCoverError = true
        }
      }, 5000)

      setTimeout(() => {
        this.isTimeout = true
      }, 3000)
    }

    // Периодическая проверка состояния буферизации (проверяем буфер каждые 200мс)
    this.bufferingCheckInterval = setInterval(() => {
      this.checkBufferingStatus()
    }, 200)
  },
  beforeUnmount() {
    this.removeFullscreenListeners()
    this.clearControlsHideTimer()
    this.clearControlsInactivityTimer()
    if (this.bufferingCheckInterval) {
      clearInterval(this.bufferingCheckInterval)
      this.bufferingCheckInterval = null
    }
  },
  methods: {
    // Функция для преобразования внешних URL в локальные прокси (только в dev режиме)
    getProxiedUrl(url) {
      if (!url) return url

      // В production режиме возвращаем URL как есть
      if (!import.meta.env.DEV) {
        return url
      }

      // В dev режиме используем прокси
      // Если это static.chapterfour.ru, используем локальный прокси
      if (url.includes('static.chapterfour.ru')) {
        return url.replace('https://static.chapterfour.ru', '/cdn')
      }

      // Если это api.chapterfour.ru, используем локальный прокси
      if (url.includes('api.chapterfour.ru')) {
        return url.replace('https://api.chapterfour.ru', '/api')
      }

      return url
    },
    async loadAndCacheCover() {
      if (!this.coverImage) return

      try {
        const proxiedUrl = this.getProxiedUrl(this.coverImage)

        const cache = await caches.open('cover-cache')
        const cachedResponse = await cache.match(proxiedUrl)
        const response = cachedResponse || (await fetch(proxiedUrl))
        if (!cachedResponse) {
          cache.put(proxiedUrl, response.clone())
        }
        const blob = await response.blob()
        this.coverSrc = URL.createObjectURL(blob)
      } catch (error) {
        // Используем оригинальный URL как fallback
        this.coverSrc = this.coverImage
      }
    },
    onCoverLoad() {
      this.isCoverLoaded = true
      this.isCoverError = false
      // Убеждаемся, что обложка видна после загрузки, если видео не играет
      this.$nextTick(() => {
        if (this.video.state.playing !== 'playing' && !this.isMouseOverVideo) {
          this.showCover()
        }
      })
    },
    onCoverError() {
      this.isCoverError = true
      this.isCoverLoaded = false
    },
    hideCover() {
      if (this.$refs.coverRef) {
        this.$refs.coverRef.classList.add('hide')
      }
    },
    showCover() {
      if (this.$refs.coverRef && this.coverImage) {
        this.$refs.coverRef.classList.remove('hide')
      }
    },
    setLength(event) {
      this.video.state.length = this.$refs.videoElement.duration
    },
    onLoadedMetadata(event) {
      this.setLength(event)
      // Синхронизируем состояние muted и volume с видео элементом
      if (this.$refs.videoElement) {
        this.video.state.muted = this.$refs.videoElement.muted
        this.video.state.volume = this.$refs.videoElement.volume
        // Ползунок инвертирован: 1 = 0 громкости (внизу), 0 = 100% громкости (вверху)
        this.volumeSliderValue = this.video.state.muted ? 1 : 1 - this.video.state.volume
      }
    },
    timeUpdateFunction(event) {
      this.video.state.timeNow = this.$refs.videoElement.currentTime
      if (this.video.state.length > 0) {
        this.video.state.position = (this.video.state.timeNow / this.video.state.length) * 100
      } else {
        this.video.state.position = 0
      }
      // Проверяем буфер при обновлении времени
      this.checkBufferingStatus()
    },
    checkBufferingStatus() {
      if (!this.$refs.videoElement) {
        this.isBuffering = false
        return
      }

      const video = this.$refs.videoElement
      const currentTime = video.currentTime
      const duration = video.duration
      const buffered = video.buffered

      // Если видео еще не загружено или на паузе, не показываем спиннер
      if (!duration || video.paused || video.ended) {
        this.isBuffering = false
        return
      }

      // Проверяем, есть ли в буфере данные для следующих 3 секунд
      const targetTime = currentTime + 3
      let hasEnoughBuffer = false

      if (buffered.length > 0) {
        // Проверяем все буферизованные диапазоны
        for (let i = 0; i < buffered.length; i++) {
          const start = buffered.start(i)
          const end = buffered.end(i)

          // Если текущее время + 3 секунды находится в буферизованном диапазоне
          if (currentTime >= start && targetTime <= end) {
            hasEnoughBuffer = true
            break
          }
          // Если следующий буфер начинается до targetTime
          if (end >= targetTime) {
            hasEnoughBuffer = true
            break
          }
        }
      }

      // Скрываем спиннер, если достаточно буфера и видео играет
      // Но не показываем спиннер просто из-за недостатка буфера, если видео не остановилось
      if (hasEnoughBuffer && !video.paused) {
        this.isBuffering = false
      }
    },
    onVideoWaiting() {
      // Видео ждет загрузки данных - показываем спиннер только если видео не на паузе
      if (this.$refs.videoElement && !this.$refs.videoElement.paused) {
        this.isBuffering = true
      }
    },
    onVideoStalled() {
      // Видео остановилось из-за нехватки данных - показываем спиннер только если видео не на паузе
      if (this.$refs.videoElement && !this.$refs.videoElement.paused) {
        this.isBuffering = true
      }
    },
    onVideoCanPlay() {
      // Видео готово к воспроизведению
      this.loading = false
      this.checkBufferingStatus()
    },
    onVideoPlaying() {
      // Видео воспроизводится - скрываем спиннер
      this.isBuffering = false
      this.checkBufferingStatus()
    },
    onVideoSeeking() {
      // Начало перемотки
      if (this.$refs.videoElement && !this.$refs.videoElement.paused) {
        this.isBuffering = true
      }
    },
    onVideoSeeked() {
      // Перемотка завершена
      this.isBuffering = false
    },
    handlePlayButtonTouch(event) {
      event.stopPropagation()
      event.preventDefault()
      this.togglePlay(event)
    },
    togglePlay(event) {
      event.stopPropagation()

      // Проверяем, был ли клик на кнопке play (для мобильных устройств)
      if (
        event.target &&
        event.target.closest &&
        event.target.closest('.video-player__play-pause-button')
      ) {
        // Клик был на кнопке, обрабатываем его
      } else if (
        !this.hideVideoControls &&
        this.shouldShowPlayPauseButton &&
        event.target === this.$refs.videoElement
      ) {
        // Если кнопка play видна и клик был на видео, игнорируем его (кнопка должна обработать)
        return
      }

      if (this.$refs.videoElement.paused) {
        // При первом запуске включаем muted и опускаем ползунок только для страницы автора
        if (!this.hasPlayedOnce && this.autoPlayOnHover) {
          this.video.state.muted = true
          this.$refs.videoElement.muted = true
          this.volumeSliderValue = 1 // Ползунок внизу (звук выключен)
        }
        this.$refs.videoElement.play()
        // Скрываем обложку при воспроизведении
        setTimeout(() => {
          this.hideCover()
        }, 200)
      } else {
        this.$refs.videoElement.pause()
      }
    },
    timeChangeBack() {
      this.$refs.videoElement.currentTime -= 10 // Перемотка назад на 10 секунд
    },
    timeChangeFw() {
      this.$refs.videoElement.currentTime += 10 // Перемотка вперед на 10 секунд
    },
    seekTo(event) {
      this.$refs.videoElement.currentTime = parseFloat(event.target.value)
    },
    toggleMute(event) {
      event.stopPropagation()
      const wasMuted = this.video.state.muted
      this.video.state.muted = !this.video.state.muted
      this.$refs.videoElement.muted = this.video.state.muted

      // Анимация ползунка при изменении состояния mute
      if (this.video.state.muted) {
        // Включаем mute - анимированно опускаем ползунок до 1 (ползунок инвертирован: 1 = 0 громкости)
        this.animateVolumeSlider(this.volumeSliderValue, 1)
      } else {
        // Выключаем mute - анимированно поднимаем ползунок до текущего значения volume
        const targetValue = 1 - this.video.state.volume
        this.animateVolumeSlider(this.volumeSliderValue, targetValue)
      }
    },
    animateVolumeSlider(from, to) {
      // Очищаем предыдущий таймер анимации
      if (this.volumeAnimationTimer) {
        clearInterval(this.volumeAnimationTimer)
      }

      const duration = 300 // Длительность анимации в миллисекундах
      const steps = 30 // Количество шагов анимации
      const stepDuration = duration / steps
      const stepValue = (to - from) / steps

      let currentStep = 0
      this.volumeSliderValue = from

      this.volumeAnimationTimer = setInterval(() => {
        currentStep++
        this.volumeSliderValue = from + stepValue * currentStep

        if (currentStep >= steps) {
          this.volumeSliderValue = to
          clearInterval(this.volumeAnimationTimer)
          this.volumeAnimationTimer = null
        }
      }, stepDuration)
    },
    setVolume(event) {
      event.stopPropagation()
      // Останавливаем анимацию, если она идет
      if (this.volumeAnimationTimer) {
        clearInterval(this.volumeAnimationTimer)
        this.volumeAnimationTimer = null
      }

      const sliderValue = parseFloat(event.target.value)
      this.volumeSliderValue = sliderValue
      const volume = 1 - sliderValue
      this.video.state.volume = volume
      this.$refs.videoElement.volume = volume
      // Если громкость больше 0, снимаем mute
      if (volume > 0 && this.video.state.muted) {
        this.video.state.muted = false
        this.$refs.videoElement.muted = false
      }
      // Если громкость 0, включаем mute
      if (volume === 0 && !this.video.state.muted) {
        this.video.state.muted = true
        this.$refs.videoElement.muted = true
      }
    },
    handleVolumeSliderLeave(event) {
      // Скрываем слайдер, когда мышь покидает контейнер
      const container = event.currentTarget
      const relatedTarget = event.relatedTarget

      // Проверяем, что мышь не переходит на дочерний элемент контейнера
      if (!relatedTarget || !container.contains(relatedTarget)) {
        this.showVolumeSlider = false
        this.scheduleBottomControlsHide()
      }
    },
    handleUserActivity() {
      if (this.video.state.playing !== 'playing') return
      this.controlsAutoHidden = false
      this.scheduleBottomControlsHide()
    },
    scheduleBottomControlsHide() {
      this.clearControlsInactivityTimer()
      if (
        this.video.state.playing !== 'playing' ||
        this.isMouseOverControls ||
        this.showVolumeSlider
      ) {
        this.controlsAutoHidden = false
        return
      }
      this.controlsInactivityTimer = setTimeout(() => {
        if (
          this.video.state.playing === 'playing' &&
          !this.isMouseOverControls &&
          !this.showVolumeSlider
        ) {
          this.controlsAutoHidden = true
        }
      }, 2000)
    },
    clearControlsInactivityTimer() {
      if (this.controlsInactivityTimer) {
        clearTimeout(this.controlsInactivityTimer)
        this.controlsInactivityTimer = null
      }
    },
    toggleFullscreen(event) {
      event.stopPropagation()
      if (!document.fullscreenElement) {
        // Входим в полноэкранный режим
        const player = this.$el
        if (player.requestFullscreen) {
          player.requestFullscreen()
        } else if (player.webkitRequestFullscreen) {
          player.webkitRequestFullscreen()
        } else if (player.msRequestFullscreen) {
          player.msRequestFullscreen()
        } else if (player.mozRequestFullScreen) {
          player.mozRequestFullScreen()
        }
      } else {
        // Выходим из полноэкранного режима
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        }
      }
    },
    setupFullscreenListeners() {
      this.fullscreenChangeHandler = () => {
        this.video.state.isFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement ||
          document.mozFullScreenElement
        )
      }
      document.addEventListener('fullscreenchange', this.fullscreenChangeHandler)
      document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler)
      document.addEventListener('msfullscreenchange', this.fullscreenChangeHandler)
      document.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler)
    },
    removeFullscreenListeners() {
      if (this.fullscreenChangeHandler) {
        document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler)
        document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler)
        document.removeEventListener('msfullscreenchange', this.fullscreenChangeHandler)
        document.removeEventListener('mozfullscreenchange', this.fullscreenChangeHandler)
      }
    },
    handleVideoMouseEnter() {
      // Скрываем обложку при наведении
      if (this.coverImage && this.$refs.coverRef) {
        this.hideCover()
      }

      if (this.autoPlayOnHover) {
        // Автозапуск при наведении для страницы автора
        if (this.$refs.videoElement && this.$refs.videoElement.paused) {
          // При первом запуске включаем muted и опускаем ползунок
          if (!this.hasPlayedOnce) {
            this.video.state.muted = true
            this.$refs.videoElement.muted = true
            this.volumeSliderValue = 1 // Ползунок внизу (звук выключен)
          }
          this.$refs.videoElement.play().catch((err) => {
            console.error('Ошибка при автозапуске видео:', err)
          })
        }
      }
      this.onVideoMouseEnter()
    },
    handleVideoMouseLeave() {
      // Показываем обложку обратно при убирании мыши, если видео не играет
      if (this.video.state.playing !== 'playing') {
        this.showCover()
      }
      // Для страницы автора видео останавливается при убирании курсора, но не если курсор на контролах
      if (
        this.autoPlayOnHover &&
        this.$refs.videoElement &&
        !this.$refs.videoElement.paused &&
        !this.isMouseOverControls
      ) {
        this.$refs.videoElement.pause()
      }
      this.onVideoMouseLeave()
    },
    handlePlayerMouseLeave() {
      // Курсор ушёл за пределы всего плеера — показываем подсказку для шоурила
      if (this.autoPlayOnHover) {
        try {
          // Более короткий тост, чтобы не мешал просмотру
          showToast('Showreel запускается при наведении курсора', 2000)
        } catch (_e) {
          // игнорируем ошибки показа тоста
        }
      }
    },
    handleControlsMouseEnter() {
      this.isMouseOverControls = true
      this.controlsAutoHidden = false
      this.clearControlsInactivityTimer()
      // Автозапуск при наведении на контролы для страницы автора
      if (this.autoPlayOnHover) {
        if (this.$refs.videoElement && this.$refs.videoElement.paused) {
          // При первом запуске включаем muted и опускаем ползунок
          if (!this.hasPlayedOnce) {
            this.video.state.muted = true
            this.$refs.videoElement.muted = true
            this.volumeSliderValue = 1 // Ползунок внизу (звук выключен)
          }
          this.$refs.videoElement.play().catch((err) => {
            console.error('Ошибка при автозапуске видео:', err)
          })
        }
      }
    },
    handleControlsMouseLeave() {
      this.isMouseOverControls = false
      this.scheduleBottomControlsHide()
      // Если курсор убрали с контролов и он не на видео, останавливаем видео для страницы автора
      if (
        this.autoPlayOnHover &&
        this.$refs.videoElement &&
        !this.$refs.videoElement.paused &&
        !this.isMouseOverVideo
      ) {
        this.$refs.videoElement.pause()
      }
    },
    onVideoMouseEnter() {
      this.isMouseOverVideo = true
      this.showControls = true
      // Если видео воспроизводится, скрываем кнопки даже при наведении
      // startControlsHideTimer сам очистит предыдущий таймер
      if (this.video.state.playing === 'playing') {
        // При повторном наведении (wasMouseOnVideo === true) используем 3 секунды, при первом - 1 секунду
        this.startControlsHideTimer()
        // Отмечаем, что мышь была на видео (для следующего повторного наведения)
        this.wasMouseOnVideo = true
      } else {
        // Если видео на паузе, просто очищаем таймер
        this.clearControlsHideTimer()
      }
    },
    onVideoMouseLeave() {
      this.isMouseOverVideo = false
      // Показываем обложку обратно, если видео не играет
      if (this.video.state.playing !== 'playing') {
        this.showCover()
      }
      // Не сбрасываем wasMouseOnVideo, чтобы при следующем наведении было повторное
      this.startControlsHideTimer()
    },
    onControlMouseEnter() {
      this.isMouseOverVideo = true
      this.showControls = true
      // Если видео воспроизводится, скрываем кнопки даже при наведении
      // startControlsHideTimer сам очистит предыдущий таймер
      if (this.video.state.playing === 'playing') {
        // При повторном наведении (wasMouseOnVideo === true) используем 3 секунды, при первом - 1 секунду
        this.startControlsHideTimer()
        // Отмечаем, что мышь была на видео (для следующего повторного наведения)
        this.wasMouseOnVideo = true
      } else {
        // Если видео на паузе, просто очищаем таймер
        this.clearControlsHideTimer()
      }
    },
    onControlMouseLeave() {
      this.isMouseOverVideo = false
      this.startControlsHideTimer()
    },
    startControlsHideTimer(delay = null) {
      this.clearControlsHideTimer()
      // Не скрываем кнопки, если видео на паузе
      if (this.video.state.playing === 'paused') {
        return
      }
      // Если delay не указан, используем 2 секунды при повторном наведении (когда мышь уже была на видео), иначе 1 секунду
      const hideDelay = delay !== null ? delay : this.wasMouseOnVideo ? 2000 : 1000
      this.controlsHideTimer = setTimeout(() => {
        this.showControls = false
      }, hideDelay)
    },
    onVideoPlay() {
      this.video.state.playing = 'playing'
      // Отмечаем, что видео было запущено хотя бы раз
      if (!this.hasPlayedOnce) {
        this.hasPlayedOnce = true
      }
      // Скрываем обложку при воспроизведении
      this.hideCover()
      // Скрываем кнопки при воспроизведении через 1 секунду, даже если курсор на видео
      // При первом запуске используем 1 секунду, не сбрасываем hasMouseEnteredOnce
      this.startControlsHideTimer(1000)
      this.controlsAutoHidden = false
      this.scheduleBottomControlsHide()
    },
    onVideoPause() {
      this.video.state.playing = 'paused'
      // Показываем обложку обратно при паузе, если мышь не наведена
      if (!this.isMouseOverVideo) {
        this.showCover()
      }
      // Показываем кнопки при паузе и не скрываем их автоматически
      this.showControls = true
      this.clearControlsHideTimer()
      this.controlsAutoHidden = false
      this.clearControlsInactivityTimer()
      // Сбрасываем флаг при паузе, чтобы следующее наведение считалось первым
      this.wasMouseOnVideo = false
    },
    clearControlsHideTimer() {
      if (this.controlsHideTimer) {
        clearTimeout(this.controlsHideTimer)
        this.controlsHideTimer = null
      }
    },
    formatTime(time) {
      if (!time || isNaN(time)) return { minutes: 0, seconds: '00' }
      const minutes = Math.floor(time / 60)
      const seconds = Math.floor(time % 60)
        .toString()
        .padStart(2, '0')
      return { minutes, seconds }
    },
  },
}
</script>

<style scoped>
.video-player {
  position: relative;
  max-width: 100%;
  margin: 0 auto;
}

.video-player.is-fullscreen,
.video-player:fullscreen,
.video-player:-webkit-full-screen,
.video-player:-moz-full-screen,
.video-player:-ms-fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
  background: #000 !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.video-player.is-fullscreen .video-player__aspect-ratio,
.video-player:fullscreen .video-player__aspect-ratio,
.video-player:-webkit-full-screen .video-player__aspect-ratio,
.video-player:-moz-full-screen .video-player__aspect-ratio,
.video-player:-ms-fullscreen .video-player__aspect-ratio {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  position: relative !important;
  width: 100% !important;
  height: auto !important;
  padding: 0 !important;
}

.video-player.is-fullscreen .video-player__aspect-ratio,
.video-player:fullscreen .video-player__aspect-ratio,
.video-player:-webkit-full-screen .video-player__aspect-ratio,
.video-player:-moz-full-screen .video-player__aspect-ratio,
.video-player:-ms-fullscreen .video-player__aspect-ratio {
  order: 1 !important;
}

.video-player.is-fullscreen .video-player__video,
.video-player:fullscreen .video-player__video,
.video-player:-webkit-full-screen .video-player__video,
.video-player:-moz-full-screen .video-player__video,
.video-player:-ms-fullscreen .video-player__video {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player.is-fullscreen .video-player__video__element,
.video-player:fullscreen .video-player__video__element,
.video-player:-webkit-full-screen .video-player__video__element,
.video-player:-moz-full-screen .video-player__video__element,
.video-player:-ms-fullscreen .video-player__video__element {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
  min-width: 100% !important;
  min-height: 100% !important;
  object-fit: contain !important;
  object-position: center center !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player.is-fullscreen .video-player__controls,
.video-player:fullscreen .video-player__controls,
.video-player:-webkit-full-screen .video-player__controls,
.video-player:-moz-full-screen .video-player__controls,
.video-player:-ms-fullscreen .video-player__controls {
  background: #fff !important;
  padding: 15px !important;
  margin-top: 0 !important;
  flex-shrink: 0 !important;
  width: 100% !important;
  z-index: 10 !important;
  position: relative !important;
  order: 2 !important;
}

.video-player.is-fullscreen .video-player__button,
.video-player:fullscreen .video-player__button,
.video-player:-webkit-full-screen .video-player__button,
.video-player:-moz-full-screen .video-player__button,
.video-player:-ms-fullscreen .video-player__button {
  color: #000 !important;
}

.video-player.is-fullscreen .progressbar__time,
.video-player:fullscreen .progressbar__time,
.video-player:-webkit-full-screen .progressbar__time,
.video-player:-moz-full-screen .progressbar__time,
.video-player:-ms-fullscreen .progressbar__time {
  color: #000 !important;
}

.video-player.is-fullscreen .progressbar__progress,
.video-player:fullscreen .progressbar__progress,
.video-player:-webkit-full-screen .progressbar__progress,
.video-player:-moz-full-screen .progressbar__progress,
.video-player:-ms-fullscreen .progressbar__progress {
  background-color: rgba(0, 0, 0, 0.2) !important;
  background-image: linear-gradient(to right, #000 0%, #000 100%) !important;
}

.video-player.is-fullscreen .progressbar__progress .trumb,
.video-player:fullscreen .progressbar__progress .trumb,
.video-player:-webkit-full-screen .progressbar__progress .trumb,
.video-player:-moz-full-screen .progressbar__progress .trumb,
.video-player:-ms-fullscreen .progressbar__progress .trumb {
  background-color: #000 !important;
}

.video-player.is-fullscreen .video-player__volume-slider,
.video-player:fullscreen .video-player__volume-slider,
.video-player:-webkit-full-screen .video-player__volume-slider,
.video-player:-moz-full-screen .video-player__volume-slider,
.video-player:-ms-fullscreen .video-player__volume-slider {
  background: #fff;
}

.video-player.is-fullscreen .video-player__cover,
.video-player:fullscreen .video-player__cover,
.video-player:-webkit-full-screen .video-player__cover,
.video-player:-moz-full-screen .video-player__cover,
.video-player:-ms-fullscreen .video-player__cover {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 2 !important;
}

.video-player__aspect-ratio {
  position: relative;
  width: 100%;
  height: 0;
  padding-top: 56.25%;
  overflow: hidden;
}

.video-player__video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.video-player__video__element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  cursor: pointer;
  z-index: 1;
}

.video-player__cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  opacity: 1;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.video-player__cover.hide {
  opacity: 0;
  pointer-events: none;
}

.video-player__cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.video-player__loader-cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  overflow: hidden;
  background-color: #f5f5f5;
}

.skeleton-cover {
  width: 100%;
  height: 100%;
  border-radius: 0;
  background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite linear;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.video-player__loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-player__video__control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.video-player__video__control.visible {
  opacity: 1;
  pointer-events: auto;
}

.video-player__video__control.left {
  left: 20px;
}

.video-player__video__control.right {
  right: 20px;
}

.video-player__video__control::before {
  content: '';
  width: 0;
  height: 0;
  border-style: solid;
}

.video-player__play-pause-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  height: 68px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  opacity: 0;
  transition:
    opacity 0.3s,
    transform 0.2s;
  pointer-events: none;
}

.video-player__play-pause-button.visible {
  opacity: 1;
  pointer-events: auto;
}

.video-player__play-pause-icon {
  width: 32px;
  height: 32px;
  fill: white;
}

.video-player__video__control.left::before {
  border-width: 10px 15px 10px 0;
  border-color: transparent white transparent transparent;
  margin-left: -5px;
}

.video-player__video__control.right::before {
  border-width: 10px 0 10px 15px;
  border-color: transparent transparent transparent white;
  margin-right: -5px;
}

.video-player__controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  margin-top: 10px;
  gap: 15px;
  transform: translateY(0);
  opacity: 1;
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.video-player__controls.hidden-down {
  transform: translateY(110%);
  opacity: 0;
  pointer-events: none;
}

.progressbar {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.video-player__buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.video-player__button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition:
    color 0.2s,
    transform 0.1s;
  border-radius: 4px;
}

.video-player__button:active {
  transform: scale(0.95);
}

.video-player__icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
}

.video-player__volume-container {
  position: relative;
  display: flex;
  align-items: center;
  padding-top: 110px;
  margin-top: -110px;
}

.video-player__volume-slider {
  position: absolute;
  bottom: 35%;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  margin-bottom: 10px;
  background: #fff;
  padding: 8px 0;
  border-radius: 4px;
  margin-top: -110px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.video-player__volume-slider--visible {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  pointer-events: auto;
}

.video-player__volume-input {
  width: 80px;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  outline: none;
  cursor: pointer;
  transform: rotate(90deg);
  position: relative;
}

.video-player__volume-input::-webkit-slider-runnable-track {
  width: 80px;
  height: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.video-player__volume-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  background: #000;
  border-radius: 50%;
  cursor: pointer;
  margin-top: -5px;
  transition: transform 0.1s;
}

.video-player__volume-input::-moz-range-track {
  width: 80px;
  height: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  border: none;
}

.video-player__volume-input::-moz-range-thumb {
  width: 14px;
  height: 14px;
  background: #000;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  transition: transform 0.1s;
}

.progressbar__time {
  font-size: 0.875rem;
  color: #666;
  min-width: 40px;
  text-align: center;
}

.progressbar__time.start {
  margin-left: 0.5rem;
  text-align: left;
}

.progressbar__time.end {
  text-align: right;
  font-weight: 500;
}

.progressbar__center {
  flex: 1;
  position: relative;
}

.progressbar__progress {
  width: 100%;
  height: 5px;
  background-color: #ddd;
  background-image: linear-gradient(to right, #000 0%, #000 100%);
  background-repeat: no-repeat;
  background-size: 0 100%;
  position: relative;
  border-radius: 2px;
}

.progressbar__progress .trumb {
  position: absolute;
  top: -5px;
  width: 15px;
  height: 15px;
  background-color: #000;
  border-radius: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  pointer-events: none;
}

.progressbar__seek {
  position: absolute;
  top: -5px;
  left: 0;
  width: 100%;
  height: 15px;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
  margin: 0;
}

input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  margin: 0;
}

input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 15px;
  height: 15px;
  background: #000;
  border-radius: 50%;
  cursor: pointer;
  margin-top: -5px;
}

input[type='range']::-moz-range-track {
  background: transparent;
}

input[type='range']::-moz-range-thumb {
  width: 15px;
  height: 15px;
  background: #000;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

@media (max-width: 48rem) {
  .video-player__controls {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    gap: 0.375rem;
    padding: 4px 2px 4px 0;
  }

  .progressbar {
    flex: 1 1 0;
    min-width: 0;
    gap: 0.375rem;
  }

  .progressbar__time {
    min-width: 2.25rem;
    flex-shrink: 0;
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
  }

  .progressbar__time.start {
    margin-left: 0;
  }

  .progressbar__center {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
  }

  .progressbar__seek {
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .video-player__buttons {
    flex-shrink: 0;
    gap: 4px;
  }

  .video-player__button {
    padding: 6px;
  }

  .video-player__icon {
    width: 18px;
    height: 18px;
  }
}

/* Hover только при реальном курсоре (не тач) */
@media (hover: hover) {
  .video-player.is-fullscreen .video-player__button:hover,
  .video-player:fullscreen .video-player__button:hover,
  .video-player:-webkit-full-screen .video-player__button:hover,
  .video-player:-moz-full-screen .video-player__button:hover,
  .video-player:-ms-fullscreen .video-player__button:hover {
    color: #000 !important;
    background: rgba(0, 0, 0, 0.1) !important;
  }

  .video-player__play-pause-button:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translate(-50%, -50%) scale(1.1);
  }

  .video-player__button:hover {
    color: #000;
    background: rgba(0, 0, 0, 0.05);
  }

  .video-player__volume-input::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  .video-player__volume-input::-moz-range-thumb:hover {
    transform: scale(1.2);
  }
}
</style>

<style>
.video-player:fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
  background: #000 !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.video-player:fullscreen .video-player__aspect-ratio {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  position: relative !important;
  width: 100% !important;
  height: auto !important;
  padding: 0 !important;
  order: 1 !important;
}

.video-player:fullscreen .video-player__video {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:fullscreen .video-player__video__element {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
  min-width: 100% !important;
  min-height: 100% !important;
  object-fit: contain !important;
  object-position: center center !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:fullscreen .video-player__controls {
  background: #fff !important;
  padding: 15px !important;
  margin-top: 0 !important;
  flex-shrink: 0 !important;
  width: 100% !important;
  z-index: 10 !important;
  position: relative !important;
  order: 2 !important;
}

.video-player:fullscreen .progressbar__time {
  color: #000 !important;
}

.video-player:fullscreen .progressbar__progress {
  background-color: rgba(0, 0, 0, 0.2) !important;
  background-image: linear-gradient(to right, #000 0%, #000 100%) !important;
}

.video-player:fullscreen .progressbar__progress .trumb {
  background-color: #000 !important;
}

.video-player:fullscreen .video-player__button {
  color: #000 !important;
}

.video-player:-webkit-full-screen {
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
  background: #000 !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.video-player:-webkit-full-screen .video-player__aspect-ratio {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
}

.video-player:-webkit-full-screen .video-player__aspect-ratio {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  position: relative !important;
  width: 100% !important;
  height: auto !important;
  padding: 0 !important;
  order: 1 !important;
}

.video-player:-webkit-full-screen .video-player__video {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:-webkit-full-screen .video-player__video__element {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
  min-width: 100% !important;
  min-height: 100% !important;
  object-fit: contain !important;
  object-position: center center !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:-webkit-full-screen .video-player__controls {
  background: #fff !important;
  padding: 15px !important;
  margin-top: 0 !important;
  flex-shrink: 0 !important;
  width: 99% !important;
  z-index: 10 !important;
  position: relative !important;
  order: 2 !important;
}

.video-player:-webkit-full-screen .progressbar__time {
  color: #000 !important;
}

.video-player:-webkit-full-screen .progressbar__progress {
  background-color: rgba(0, 0, 0, 0.2) !important;
  background-image: linear-gradient(to right, #000 0%, #000 100%) !important;
}

.video-player:-webkit-full-screen .progressbar__progress .trumb {
  background-color: #000 !important;
}

.video-player:-webkit-full-screen .video-player__button {
  color: #000 !important;
}

.video-player:-moz-full-screen {
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
  background: #000 !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.video-player:-moz-full-screen .video-player__aspect-ratio {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
}

.video-player:-moz-full-screen .video-player__aspect-ratio {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  position: relative !important;
  width: 100% !important;
  height: auto !important;
  padding: 0 !important;
  order: 1 !important;
}

.video-player:-moz-full-screen .video-player__video {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:-moz-full-screen .video-player__video__element {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
  min-width: 100% !important;
  min-height: 100% !important;
  object-fit: contain !important;
  object-position: center center !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:-moz-full-screen .video-player__controls {
  background: #fff !important;
  padding: 15px !important;
  margin-top: 0 !important;
  flex-shrink: 0 !important;
  width: 100% !important;
  z-index: 10 !important;
  position: relative !important;
  order: 2 !important;
}

.video-player:-moz-full-screen .progressbar__time {
  color: #000 !important;
}

.video-player:-moz-full-screen .progressbar__progress {
  background-color: rgba(0, 0, 0, 0.2) !important;
  background-image: linear-gradient(to right, #000 0%, #000 100%) !important;
}

.video-player:-moz-full-screen .progressbar__progress .trumb {
  background-color: #000 !important;
}

.video-player:-moz-full-screen .video-player__button {
  color: #000 !important;
}

.video-player:-ms-fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
  background: #000 !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.video-player:-ms-fullscreen .video-player__aspect-ratio {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
}

.video-player:-ms-fullscreen .video-player__aspect-ratio {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  position: relative !important;
  width: 100% !important;
  height: auto !important;
  padding: 0 !important;
  order: 1 !important;
}

.video-player:-ms-fullscreen .video-player__video {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:-ms-fullscreen .video-player__video__element {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
  min-width: 100% !important;
  min-height: 100% !important;
  object-fit: contain !important;
  object-position: center center !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-player:-ms-fullscreen .video-player__controls {
  background: #fff !important;
  padding: 15px !important;
  margin-top: 0 !important;
  flex-shrink: 0 !important;
  width: 100% !important;
  z-index: 10 !important;
  position: relative !important;
  order: 2 !important;
}

.video-player:-ms-fullscreen .progressbar__time {
  color: #000 !important;
}

.video-player:-ms-fullscreen .progressbar__progress {
  background-color: rgba(0, 0, 0, 0.2) !important;
  background-image: linear-gradient(to right, #000 0%, #000 100%) !important;
}

.video-player:-ms-fullscreen .progressbar__progress .trumb {
  background-color: #000 !important;
}

.video-player:-ms-fullscreen .video-player__button {
  color: #000 !important;
}

.video-buffering {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.video-buffering__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}

.video-buffering__spinner {
  position: relative;
  z-index: 101;
}

.spinner {
  border: 0.25rem solid rgba(255, 255, 255, 0.3);
  border-top: 0.25rem solid #fff;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 48rem) {
  .video-player.is-fullscreen .video-player__controls,
  .video-player:fullscreen .video-player__controls,
  .video-player:-webkit-full-screen .video-player__controls,
  .video-player:-moz-full-screen .video-player__controls,
  .video-player:-ms-fullscreen .video-player__controls {
    padding: 8px 10px !important;
    box-sizing: border-box !important;
    min-width: 0 !important;
  }

  .video-player:-webkit-full-screen .video-player__controls {
    width: 100% !important;
  }
}

@media (hover: hover) {
  .video-player:fullscreen .video-player__button:hover {
    color: #000 !important;
    background: rgba(0, 0, 0, 0.1) !important;
  }

  .video-player:-webkit-full-screen .video-player__button:hover {
    color: #000 !important;
    background: rgba(0, 0, 0, 0.1) !important;
  }

  .video-player:-moz-full-screen .video-player__button:hover {
    color: #000 !important;
    background: rgba(0, 0, 0, 0.1) !important;
  }

  .video-player:-ms-fullscreen .video-player__button:hover {
    color: #000 !important;
    background: rgba(0, 0, 0, 0.1) !important;
  }
}
</style>
